/**
 * Achica una foto antes de subirla (Esteban, 2026-10-31).
 *
 * La foto de la clase salía del teléfono a tamaño completo (3–5 MB). Con datos móviles la subida no terminaba:
 * Maura (Acrilismo SPS) intentó guardar dos veces y las dos se cortaron a los 20–30 s. A 1600 px y calidad 0.8
 * queda en ~300 KB y se sigue leyendo bien quién está en la foto.
 *
 * Se decodifica con <img>, que respeta la orientación EXIF. Si el navegador no puede abrir el formato
 * (HEIC en Chrome/Android), tarda de más o el resultado no es más chico, se sube la original: el servidor
 * convierte HEIC a JPG. Nunca falla: en el peor caso devuelve el mismo archivo.
 */

const LADO_MAXIMO = 1600;
const CALIDAD = 0.8;
const ESPERA_MAXIMA_MS = 10000;

/** Las que ya salieron de aquí (o se recuperaron de un borrador): no se vuelven a comprimir. */
const yaComprimidas = new WeakSet<File>();

export const marcarComoComprimida = (archivo: File): File => {
	yaComprimidas.add(archivo);
	return archivo;
};

export const estaComprimida = (archivo: File): boolean => yaComprimidas.has(archivo);

const decodificar = (archivo: File): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const url = URL.createObjectURL(archivo);
		const img = new Image();
		img.onload = () => {
			URL.revokeObjectURL(url);
			resolve(img);
		};
		img.onerror = () => {
			URL.revokeObjectURL(url);
			reject(new Error("no se pudo abrir la imagen"));
		};
		img.src = url;
	});

const achicar = async (archivo: File): Promise<File> => {
	const img = await decodificar(archivo);
	const ancho = img.naturalWidth || img.width;
	const alto = img.naturalHeight || img.height;
	if (!ancho || !alto) return archivo;

	const escala = Math.min(1, LADO_MAXIMO / Math.max(ancho, alto));
	const lienzo = document.createElement("canvas");
	lienzo.width = Math.round(ancho * escala);
	lienzo.height = Math.round(alto * escala);
	const ctx = lienzo.getContext("2d");
	if (!ctx) return archivo;
	ctx.drawImage(img, 0, 0, lienzo.width, lienzo.height);

	const blob: Blob | null = await new Promise((resolve) => lienzo.toBlob(resolve, "image/jpeg", CALIDAD));
	// Suelta la memoria del lienzo ya: en un iPhone con la página pesada es lo que hace que Safari la recargue.
	lienzo.width = 0;
	lienzo.height = 0;

	if (!blob || blob.size === 0 || blob.size >= archivo.size) return archivo;

	const nombre = archivo.name.replace(/\.[^./\\]+$/, "") + ".jpg";
	return new File([blob], nombre, { type: "image/jpeg", lastModified: archivo.lastModified });
};

/** Si dos partes de la pantalla piden la misma foto a la vez, se comprime una sola vez. */
const enProceso = new WeakMap<File, Promise<File>>();

const comprimir = async (archivo: File): Promise<File> => {
	try {
		const resultado = await Promise.race<File>([
			achicar(archivo),
			new Promise<File>((resolve) => setTimeout(() => resolve(archivo), ESPERA_MAXIMA_MS))
		]);
		return marcarComoComprimida(resultado);
	} catch {
		return marcarComoComprimida(archivo);
	}
};

export const comprimirFoto = (archivo: File): Promise<File> => {
	if (estaComprimida(archivo) || !String(archivo.type || "").startsWith("image/") || archivo.type === "image/gif") {
		return Promise.resolve(archivo);
	}
	let pendiente = enProceso.get(archivo);
	if (!pendiente) {
		pendiente = comprimir(archivo);
		enProceso.set(archivo, pendiente);
	}
	return pendiente;
};
